import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import axios from "axios";
import {
  listTemplates,
  createTemplate,
  submitTemplate,
  syncTemplate,
  activateTemplate,
  deleteTemplate,
} from "../../services/templateService";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import TemplateCard from "./components/TemplateCard";
import TemplateFormModal from "./components/TemplateFormModal";
import TemplateOnboardingGuide from "./components/TemplateOnboardingGuide";
import styles from "./Templates.module.css";

/**
 * Tela de Gestão de Modelos de Mensagem (templates de WhatsApp Business).
 *
 * O dono do negócio escreve a mensagem de lembrete uma vez; o sistema cuida da
 * aprovação e do envio automático. A complexidade da Meta é abstraída:
 * - Status técnicos viram badges humanizados.
 * - O fluxo (criar → enviar → aprovar → ativar) é guiado por banners e timeline.
 *
 * Crítico: o `reminderService` só envia lembretes se houver um modelo com
 * `isActive: true` e `status: "APPROVED"`. Por isso a página destaca quando
 * nenhum modelo está ativo.
 */

const DELETE_ACTIVE_WARNING =
  "Este é o modelo em uso para lembretes. Ao excluí-lo, seus clientes deixarão de receber lembretes automáticos até você ativar outro modelo aprovado. Deseja continuar?";

const DELETE_DEFAULT_MESSAGE =
  "Tem certeza que deseja excluir este modelo? Esta ação não pode ser desfeita.";

/** Detecta se um erro do axios é cancelamento (AbortController.abort) — no-op. */
function isCanceledError(err) {
  return (
    axios.isCancel?.(err) ||
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError" ||
    err?.name === "AbortError"
  );
}

/** Extrai uma mensagem de erro legível da resposta do axios. */
function readApiError(err, fallback) {
  const data = err?.response?.data;
  if (typeof data?.error === "string") return data.error;
  if (typeof data?.message === "string") return data.message;
  // Erro de validação no formato { error: { bodyText: [...] } }.
  if (data?.error && typeof data.error === "object") {
    const first = Object.values(data.error).flat().find(Boolean);
    if (typeof first === "string") return first;
  }
  return fallback;
}

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  // Modal de criação/edição.
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null); // null = criação
  const [submitting, setSubmitting] = useState(false);

  // Ação por card em andamento: { id, action } | null.
  const [cardBusy, setCardBusy] = useState(null);

  // Diálogo de confirmação de exclusão.
  const [pendingDelete, setPendingDelete] = useState(null); // template | null
  const [deleting, setDeleting] = useState(false);

  // Cancela o re-fetch anterior em flight (criar/submeter/sync/ativar/excluir
  // disparam fetchTemplates em sequência) e evita setState pós-unmount.
  const templatesAbortRef = useRef(null);

  // ─── Fetch ─────────────────────────────────────────────────────────
  const fetchTemplates = useCallback(async () => {
    // Cancela request anterior em flight pra evitar race condition.
    templatesAbortRef.current?.abort();
    const controller = new AbortController();
    templatesAbortRef.current = controller;

    setStatus("loading");
    try {
      const res = await listTemplates({ signal: controller.signal });
      setTemplates(Array.isArray(res.data) ? res.data : []);
      setStatus("ready");
    } catch (err) {
      if (isCanceledError(err)) return;
      setStatus("error");
      toast.error("Não foi possível carregar os modelos de mensagem.");
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    return () => {
      templatesAbortRef.current?.abort();
    };
  }, [fetchTemplates]);

  // ─── Derivados ─────────────────────────────────────────────────────
  const hasTemplates = templates.length > 0;
  const activeTemplate = useMemo(
    () => templates.find((t) => t.isActive),
    [templates],
  );
  const hasActive = Boolean(activeTemplate);
  const hasPending = useMemo(
    () => templates.some((t) => t.status === "PENDING"),
    [templates],
  );
  const showOnboardingGuide = hasPending && !hasActive;

  // ─── Criar / Corrigir e reenviar (submit do modal) ─────────────────
  const handleFormSubmit = useCallback(
    async ({ displayName, bodyText, category }) => {
      setSubmitting(true);
      try {
        if (editingTemplate) {
          // Fluxo "Corrigir e reenviar" de um modelo REJECTED.
          await submitTemplate(editingTemplate.id, { bodyText, category });
          toast.success("Modelo corrigido e reenviado para aprovação.");
        } else {
          const res = await createTemplate({ displayName, bodyText, category });
          if (res.data?.warning) {
            // Salvo como rascunho (WhatsApp não conectado). O warning também
            // fica anexado ao card via re-fetch — mas a API retorna o objeto
            // sem persistir o warning, então mostramos via toast aqui.
            toast.warn(res.data.warning);
          } else {
            toast.success("Modelo enviado para aprovação!");
          }
        }
        setIsFormOpen(false);
        setEditingTemplate(null);
        await fetchTemplates();
      } catch (err) {
        toast.error(
          readApiError(
            err,
            "Não foi possível salvar o modelo. Tente novamente em instantes.",
          ),
        );
      } finally {
        setSubmitting(false);
      }
    },
    [editingTemplate, fetchTemplates],
  );

  // ─── Enviar DRAFT para aprovação (sem body) ────────────────────────
  const handleSubmitDraft = useCallback(
    async (template) => {
      setCardBusy({ id: template.id, action: "submit" });
      try {
        await submitTemplate(template.id);
        toast.success("Modelo enviado para aprovação!");
        await fetchTemplates();
      } catch (err) {
        toast.error(
          readApiError(err, "Não foi possível enviar o modelo para aprovação."),
        );
      } finally {
        setCardBusy(null);
      }
    },
    [fetchTemplates],
  );

  // ─── Verificar status (sync) ───────────────────────────────────────
  const handleSync = useCallback(
    async (template) => {
      setCardBusy({ id: template.id, action: "sync" });
      try {
        await syncTemplate(template.id);
        await fetchTemplates();
        toast.success("Status atualizado.");
      } catch (err) {
        toast.error(
          readApiError(err, "Não foi possível verificar o status agora."),
        );
      } finally {
        setCardBusy(null);
      }
    },
    [fetchTemplates],
  );

  // ─── Ativar modelo ─────────────────────────────────────────────────
  const handleActivate = useCallback(
    async (template) => {
      setCardBusy({ id: template.id, action: "activate" });
      try {
        const res = await activateTemplate(template.id);
        toast.success(res.data?.message || "Modelo ativado!");
        await fetchTemplates();
      } catch (err) {
        toast.error(readApiError(err, "Não foi possível ativar o modelo."));
      } finally {
        setCardBusy(null);
      }
    },
    [fetchTemplates],
  );

  // ─── Editar (abre modal pré-preenchido para REJECTED) ──────────────
  const handleEdit = useCallback((template) => {
    setEditingTemplate(template);
    setIsFormOpen(true);
  }, []);

  // ─── Excluir (confirmação) ─────────────────────────────────────────
  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setDeleting(true);
    setCardBusy({ id: target.id, action: "delete" });
    try {
      const res = await deleteTemplate(target.id);
      toast.success(res.data?.message || "Modelo excluído.");
      setPendingDelete(null);
      await fetchTemplates();
    } catch (err) {
      toast.error(readApiError(err, "Não foi possível excluir o modelo."));
    } finally {
      setDeleting(false);
      setCardBusy(null);
    }
  }, [pendingDelete, fetchTemplates]);

  const openCreateModal = useCallback(() => {
    setEditingTemplate(null);
    setIsFormOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    if (submitting) return;
    setIsFormOpen(false);
    setEditingTemplate(null);
  }, [submitting]);

  const busyActionFor = (id) =>
    cardBusy && cardBusy.id === id ? cardBusy.action : null;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <span aria-hidden="true">💬</span> Modelos de Mensagem
          </h1>
          <p className={styles.subtitle}>
            Gerencie a mensagem que seu assistente envia para lembrar os clientes
            dos agendamentos.
          </p>
        </div>
        {hasTemplates && (
          <button
            type="button"
            className={styles.createBtn}
            onClick={openCreateModal}
          >
            + Criar modelo
          </button>
        )}
      </header>

      {/* ─── Banner contextual ──────────────────────────────────────── */}
      {status === "ready" && !hasTemplates && (
        <div className={styles.bannerOnboarding} role="region" aria-label="Comece por aqui">
          <span className={styles.bannerIcon} aria-hidden="true">
            💬
          </span>
          <div className={styles.bannerBody}>
            <h2 className={styles.bannerTitle}>
              Configure sua mensagem de lembrete
            </h2>
            <p className={styles.bannerText}>
              Sem um modelo de mensagem aprovado, seus clientes não recebem
              lembretes automáticos de agendamento.
            </p>
            <button
              type="button"
              className={styles.bannerCta}
              onClick={openCreateModal}
            >
              Criar meu primeiro modelo
            </button>
          </div>
        </div>
      )}

      {status === "ready" && hasTemplates && !hasActive && (
        <div className={styles.bannerWarning} role="alert">
          <span className={styles.bannerIcon} aria-hidden="true">
            ⚠️
          </span>
          <div className={styles.bannerBody}>
            <h2 className={styles.bannerWarningTitle}>Nenhum modelo ativo</h2>
            <p className={styles.bannerText}>
              Você tem modelos criados, mas nenhum está sendo usado para enviar
              lembretes. Ative um modelo aprovado para os lembretes voltarem a
              funcionar.
            </p>
          </div>
        </div>
      )}

      {/* ─── Timeline pós-criação ───────────────────────────────────── */}
      {status === "ready" && showOnboardingGuide && <TemplateOnboardingGuide />}

      {/* ─── Loading (skeleton) ─────────────────────────────────────── */}
      {status === "loading" && (
        <ul className={styles.list} aria-busy="true" aria-label="Carregando modelos">
          {[0, 1].map((i) => (
            <li key={i} className={styles.skeleton} aria-hidden="true" />
          ))}
        </ul>
      )}

      {/* ─── Erro ───────────────────────────────────────────────────── */}
      {status === "error" && (
        <div className={styles.errorBox} role="alert">
          <span>Não foi possível carregar os modelos de mensagem.</span>
          <button
            type="button"
            className={styles.retryBtn}
            onClick={fetchTemplates}
          >
            Tentar novamente
          </button>
        </div>
      )}

      {/* ─── Lista ──────────────────────────────────────────────────── */}
      {status === "ready" && hasTemplates && (
        <ul className={styles.list}>
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              busyAction={busyActionFor(template.id)}
              onSubmit={() => handleSubmitDraft(template)}
              onSync={() => handleSync(template)}
              onActivate={() => handleActivate(template)}
              onEdit={() => handleEdit(template)}
              onDelete={() => setPendingDelete(template)}
            />
          ))}
        </ul>
      )}

      {/* ─── Modal de criação/edição ────────────────────────────────── */}
      <TemplateFormModal
        isOpen={isFormOpen}
        template={editingTemplate}
        submitting={submitting}
        onSubmit={handleFormSubmit}
        onClose={closeModal}
      />

      {/* ─── Confirmação de exclusão ────────────────────────────────── */}
      <ConfirmDialog
        isOpen={Boolean(pendingDelete)}
        title="Excluir modelo"
        message={
          pendingDelete?.isActive
            ? DELETE_ACTIVE_WARNING
            : DELETE_DEFAULT_MESSAGE
        }
        confirmLabel={deleting ? "Excluindo..." : "Excluir"}
        cancelLabel="Cancelar"
        confirmVariant="danger"
        confirmDisabled={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!deleting) setPendingDelete(null);
        }}
      />
    </div>
  );
}
